import React from "react";

import {  type Profile,  type User } from "@/.zenstack/logical-prisma-client";
import { useSession } from "@/lib/auth/client";
import { type SessionUser } from "@/lib/auth/types";

import { Avatar, AvatarImage, AvatarFallback } from "./avatar";


// Generic user type for flexible avatar display
type UserLike = {
  id?: string;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  image?: string | null;
  avatarFile?: { url?: string };
  avatarUrl?: string;
  hue?: number;
};

interface ApartmentDibsAvatarProps {
  user?: User | SessionUser | UserLike;
  className?: string;
  optimisticImageUrl?: string | null;
}

const getInitials = (firstName?: string | null, lastName?: string | null, fullName?: string | null) => {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`;
  }

  if (fullName) {
    const names = fullName.split(' ').filter(name => name.length > 0);
    if (names.length === 1 && names[0]) {
      // Single name: take first letter
      return names[0][0];
    } else if (names.length >= 2 && names[0] && names[names.length - 1]) {
      // Multiple names: take first letter of first name and first letter of last name
      return `${names[0][0]}${names[names.length - 1]![0]}`;
    }
  }
  // No initials available
  return '';
};

// Define a type guard to check if user is of type User
const isUser = (user: unknown): user is User => {
  return (
    typeof user === "object" && 
    user !== null && 
    "name" in user && 
    typeof (user as Record<string, unknown>).name === "string"
  );
};

// Define a type guard to check if user is of type Profile
// Note: Profile no longer has firstName/lastName (moved to User model)
const isProfile = (user: unknown): user is Profile & { avatarFile?: { url: string } } => {
  return (
    typeof user === "object" &&
    user !== null &&
    "emails" in user &&
    Array.isArray((user as Record<string, unknown>).emails)
  );
};

const ApartmentDibsAvatar: React.FC<ApartmentDibsAvatarProps> = ({ user, className, optimisticImageUrl }) => {
  const { data: session } = useSession();
  const sessionUser = session?.user;

  let firstName: string | null = null;
  let lastName: string | null = null;
  let fullName: string | null = null;
  let imageUrl: string | null = null;

  // Avatar image selection logic - prefer uploaded file over User.image, fallback to session
  if (optimisticImageUrl) {
    // Use optimistic URL for immediate display
    imageUrl = optimisticImageUrl;
  } else if (user) {
    // Check user object directly for image field (works for all user types)
    const userWithImage = user as { 
      image?: string | null; 
      avatarFile?: { url?: string };
      avatarUrl?: string;
    };
    imageUrl = userWithImage.avatarFile?.url || userWithImage.avatarUrl || userWithImage.image || null;
  } else if (sessionUser) {
    // Fallback to session user
    // TODO: avatarFile relationship is not cached in Better Auth session (ADR-004)
    // Need to query avatarFile separately if needed, for now just use image field
    imageUrl = sessionUser.image || null;
  } else {
    // No user or session data
    imageUrl = null;
  }

  // Name logic - separate from avatar selection
  if (user && isUser(user)) {
    fullName = user.name || null;
  } else if (user && isProfile(user)) {
    fullName = null;
  } else if (user) {
    // Handle UserLike type - extract name fields safely
    const userLike = user as UserLike;
    firstName = userLike.firstName || null;
    lastName = userLike.lastName || null;
    fullName = userLike.name || null;
  } else if (sessionUser) {
    // TODO: firstName/lastName are not cached in Better Auth session (ADR-004)
    // Need to query User model separately if needed, for now just use name
    fullName = sessionUser.name || null;
  }

  const [loadingStatus, setLoadingStatus] = React.useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');

  const initials = getInitials(firstName, lastName, fullName);

  return (
    <Avatar className={className}>
      {imageUrl && (
        <AvatarImage
          src={imageUrl}
          onLoadingStatusChange={(status) => setLoadingStatus(status)}
        />
      )}
      <AvatarFallback>
        {imageUrl && (loadingStatus === 'idle' || loadingStatus === 'loading') ? (
          <div className="h-full w-full animate-pulse rounded-full bg-muted" />
        ) : initials ? (
          initials
        ) : (
          <div className="h-full w-full animate-pulse rounded-full bg-muted" />
        )}
      </AvatarFallback>
    </Avatar>
  );
};

export default ApartmentDibsAvatar; 